import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Lock, RefreshCw, Sparkles } from 'lucide-react';

/**
 * ConfidentialPrayersPanel
 * Restricted to the pastoral whitelist (jason@ + andy@). Lists visibility=
 * 'confidential' prayers. These never touch the public wall — the author's
 * request is delivered directly to the pastors and lives only in this view.
 */
const ConfidentialPrayersPanel = () => {
  const { API } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(null); // null = still checking

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/prayers/confidential`);
      setItems(res.data || []);
    } catch (e) {
      // 403 → viewer isn't on the whitelist; hide silently instead of toasting.
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.detail || 'Failed to load confidential prayers');
      }
    } finally {
      setLoading(false);
    }
  }, [API]);

  // Gate on the /can-view helper so non-viewers never see the panel at all.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/prayers/confidential/can-view`);
        if (cancelled) return;
        const allowed = !!res.data?.can_view;
        setCanView(allowed);
        if (allowed) fetchItems();
      } catch {
        if (!cancelled) setCanView(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API, fetchItems]);

  const markAnswered = async (prayer) => {
    try {
      await axios.put(`${API}/prayers/${prayer.id}`, { answered: !prayer.answered });
      toast.success(prayer.answered ? 'Unmarked as answered' : 'Marked as answered');
      fetchItems();
    } catch {
      toast.error('Failed to update prayer');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Hide the whole panel when the user isn't on the pastoral whitelist.
  if (canView === false) return null;

  return (
    <Card data-testid="confidential-prayers-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
              <Lock className="w-5 h-5" />
              Confidential Requests
            </CardTitle>
            <CardDescription>
              Sensitive requests. Only the pastors on the confidential whitelist can see these — never posted publicly, never shown to prayer moderators.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchItems} data-testid="confidential-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="confidential-empty">
            No confidential prayer requests right now.
          </p>
        ) : (
          items.map((p) => (
            <div
              key={p.id}
              className="p-3 rounded-lg border border-slate-200 bg-slate-50"
              data-testid={`confidential-prayer-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div>
                  <p className="font-medium text-slate-800">{p.user_name}</p>
                  <p className="text-xs text-slate-500">{formatDate(p.created_at)}</p>
                </div>
                {p.answered && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Praise Report
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{p.prayer_text}</p>
              {(p.updates || []).length > 0 && (
                <div className="space-y-2 mb-3">
                  {p.updates.map((u, idx) => (
                    <div key={idx} className="pl-3 border-l-2 border-[#2B6B7F]/40 text-sm">
                      <p className="text-xs text-slate-500 mb-0.5">
                        Update · {formatDate(u.created_at)}
                      </p>
                      <p className="text-slate-700 whitespace-pre-wrap">{u.text}</p>
                    </div>
                  ))}
                </div>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => markAnswered(p)}
                data-testid={`confidential-mark-answered-${p.id}`}
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                {p.answered ? 'Unmark' : 'Mark as Answered'}
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default ConfidentialPrayersPanel;
